import { Component, OnInit, Input, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-bc-pr-result-table',
  imports: [],
  templateUrl: './bc-pr-result-table.component.html',
  styleUrl: './bc-pr-result-table.component.scss'
})
export class BcPrResultTableComponent implements OnInit, OnChanges {
  @Input() blend1Details: string[] = [];  // Blend 1 prediction data
  @Input() blend2Details: string[] = [];  // Blend 2 prediction data
  @Input() j300_1Details: string[] = [];
  @Input() j300_2Details: string[] = [];

  @ViewChild('methodPanel') methodPanel!: TemplateRef<unknown>;

  tables: any[] = []; // Array to handle multiple tables

  constructor(private offcanvas: NgbOffcanvas) { }
  ngOnInit() {
    this.createTable();
  }

  /** Property/method pairs for the reference panel, ordered like the table rows. */
  get methodRows(): { property: string; method: string; indent: boolean }[] {
    if (this.tables.length === 0) {
      return [];
    }
    return this.tables[0].rows.map((row: any) => ({
      property: row.properties,
      method: row.method ? row.method : '—',
      indent: !!row.indent
    }));
  }

  openMethodsPanel(): void {
    this.offcanvas.open(this.methodPanel, {
      position: 'end',
      panelClass: 'method-panel',
      ariaLabelledBy: 'method-panel-title'
    });
  }

  ngOnChanges(): void {
    this.updateTable();
  }

  createTable() {
    this.tables.push(
      {
        headers: ['Properties', /*'Method',*/ 'Unit', 'Blend 1', 'J300', 'Blend 2', 'J300'],
        rows: [
          { properties: 'KV100', method: 'ASTM D445', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'KV40', method: 'ASTM D445', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'VI', method: 'ASTM D2270', unit: '', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'KV20', method: 'ASTM D341', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'HTHS 150C', method: 'ASTM D4683', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'HTHS 100C', method: 'ASTM D4683', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'KV100 (30 Cycles Sheared)', method: 'ASTM D7109', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'KV100 (90 Cycles Sheared)', method: 'ASTM D7109', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '' },
          { properties: 'CCS', method: 'ASTM D5293', unit: '', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: '-10C (25W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: '-15C (20W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: '-20C (15W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: '-25C (10W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: '-30C (5W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: '-35C (0W)', method: '', unit: 'cP', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: true },
          { properties: 'NOACK', method: 'ASTM D5800', unit: '%', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'BOBV (100C)', method: 'ASTM D445', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'BOBV (40C)', method: 'ASTM D445', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: ' ', indent: false },
          { properties: 'BOB VI', method: 'ASTM D2270', unit: 'cSt', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'BOB Saturates', method: 'ASTM D2007', unit: '%', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false },
          { properties: 'BOB Sulfur', method: 'ASTM D2622', unit: '%', blend1: '', j300_0: '', blend2: '', j300_1: '', indent: false }
        ]
      },
    )
  }

  updateTable() {
    if (this.tables.length > 0) {
      const table = this.tables[0];

      // Check whether there is real data (not just empty string arrays)
      const hasBlend1Data = this.hasActualData(this.blend1Details);
      const hasBlend2Data = this.hasActualData(this.blend2Details);

      for (let i = 0; i < table.rows.length; i++) {
        const row = table.rows[i];
        const isCCS = row.properties === 'CCS';

        // Blend 1: If there is data, show 'NaN' for empty values (except CCS); if there is no data, show empty
        if (hasBlend1Data) {
          const blend1Value = this.blend1Details[i] || '';
          row.blend1 = (blend1Value === '' && !isCCS) ? 'NaN' : blend1Value;
        } else {
          row.blend1 = '';
        }

        // Blend 2: If there is data, show 'NaN' for empty values (except CCS); if there is no data, show empty
        if (hasBlend2Data) {
          const blend2Value = this.blend2Details[i] || '';
          row.blend2 = (blend2Value === '' && !isCCS) ? 'NaN' : blend2Value;
        } else {
          row.blend2 = '';
        }

        // Update with J300 grades data (no changes)
        row.j300_0 = this.j300_1Details[i] || '';
        row.j300_1 = this.j300_2Details[i] || '';
      }
    }
  }

  /**
  * Verifies whether an array has at least one non-empty value (real data)
  * Distinguishes an empty array [] from an array of empty values ['', '', ...]
   */
  hasActualData(array: string[]): boolean {
    return array.length > 0 && array.some(value => value !== '');
  }
}
